import * as S from './styles';
import { colors } from '@/constants';
import { formatCurrency } from '@/shared';
import { Modal, ToolTip } from '@/components';
import { FaChevronDown } from 'react-icons/fa6';
import { CHECKOUT } from '@/constants/systemRoutes';
import { usePayments } from '@/talons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobals, useCart, useCartTotals, useDiscountAmountFlex, useUserContext, useClient } from '@/hooks';

export const CartCheckoutBlock = () => {
  // USE HOOKS
  const { clientPayments, currentPayment } = usePayments();
  const { rcaSaldoFlex, valorMinimo } = useUserContext();
  const { cartTotals, fetchTotalPriceByPaymentCode } = useCartTotals();
  const { totalQuantity, totalVolume, hasOrderNumberSaved } = useCart();
  const { paymentCode, setPaymentCode, setCanAccessCheckout, scrollDirection } = useGlobals();
  const { discountAmountAll, estimatedSaldoFlexClient, currentSaldoFlexClient } = useDiscountAmountFlex();

  // USE STATE
  const [modalOpen, setModalOpen] = useState(false);
  const [tooltipOpen, setTooltip] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPaymentCode, setSelectedPaymentCode] = useState(paymentCode);

  const cartPrices = useMemo(() => {
    return {
      total: formatCurrency({ value: cartTotals?.total }),
      subtotal: formatCurrency({ value: cartTotals?.subtotal }),
      st_price: formatCurrency({ value: cartTotals?.st_price }),
    };
  }, [cartTotals]);

  const saldoFlex = rcaSaldoFlex || 0;

  // Substitui cálculo manual por campo do contexto (mantém fallback)
  const novoSaldoFlex = currentSaldoFlexClient ?? estimatedSaldoFlexClient ?? saldoFlex + discountAmountAll;

  const isCartEmpty = !cartTotals?.total;
  const pedidoJaEfetuado = !hasOrderNumberSaved; // se não houver order number, significa que já foi finalizado
  const abaixoValorMinimo = (cartTotals?.total || 0) < valorMinimo;

  // Se o saldo flex for positivo, o novo saldo flex deve ser maior ou igual a 0
  const flexPositivoValido = saldoFlex > 0 && novoSaldoFlex >= 0;

  // Se o saldo flex for negativo, o novo saldo flex deve ser maior que o saldo flex atual
  const flexNegativoValido = saldoFlex <= 0 && novoSaldoFlex > saldoFlex;
  const flexValido = !discountAmountAll ? true : flexPositivoValido || flexNegativoValido;
  const isCheckoutDisabled = isCartEmpty || pedidoJaEfetuado || !flexValido || abaixoValorMinimo || isLoading;

  // USE CALLBACK
  const setGlobalPaymentCode = useCallback(() => {
    setPaymentCode(selectedPaymentCode);
    setModalOpen(false);
  }, [selectedPaymentCode, setPaymentCode]);

  const handleTooltip = useCallback(() => {
    if (saldoFlex + discountAmountAll >= saldoFlex && Number(cartTotals?.total || 0) >= valorMinimo) return;

    setTooltip(true);
  }, [saldoFlex, discountAmountAll, cartTotals?.total, valorMinimo, setTooltip]);

  // USE EFFECT
  useEffect(() => {
    if (modalOpen) setSelectedPaymentCode(paymentCode);
  }, [modalOpen, paymentCode]);

  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  return (
    <S.Container $scrolled={scrollDirection === 'down'}>
      <S.Button onClick={() => setModalOpen(true)} data-test="component-search-button-open-payment">
        <S.PaymentCondition>
          <S.Icon size={20} color={colors.brand.grafite} />

          <S.PaymentSelected>{currentPayment}</S.PaymentSelected>
        </S.PaymentCondition>

        <FaChevronDown color={colors.brand.grafite} />
      </S.Button>

      <S.TotalsContainer>
        <S.TotalLabel>
          Total
          <S.CartQuantityLabel>
            ({totalQuantity} {totalQuantity > 1 ? 'itens' : 'item'} e {totalVolume} {totalVolume > 1 ? 'volumes' : 'volume'})
          </S.CartQuantityLabel>
        </S.TotalLabel>
        <S.Total>{cartPrices?.total}</S.Total>
      </S.TotalsContainer>

      <ToolTip
        show={tooltipOpen || pedidoJaEfetuado}
        callback={handleTooltip}
        displayMode="click"
        text={
          pedidoJaEfetuado
            ? 'Pedido já efetuado, crie um novo carrinho.'
            : abaixoValorMinimo
              ? `Pedido mínimo de ${formatCurrency({ value: valorMinimo })}`
              : `Limite Flex indisponível, por favor verifique os itens do carrinho.`
        }
      >
        <S.LinkButton
          $disabled={isCheckoutDisabled}
          onClick={() => setCanAccessCheckout(true)}
          data-test={'checkout-button'}
          to={CHECKOUT.link}
        >
          <S.ButtonLabel>Fechar pedidos</S.ButtonLabel>
        </S.LinkButton>
      </ToolTip>

      <Modal id="carrinho-mudar-condicoes-pagamento" closeModal={setModalOpen} isOpen={modalOpen} bottomMargin={60}>
        <S.ModalContent>
          <S.ModalTitle>Prazos e Formas de pagamento</S.ModalTitle>

          <S.ListBg>
            {clientPayments?.map((item, ind: number) => {
              const isActive = item.cod_condicao === selectedPaymentCode;
              const priceCond = fetchTotalPriceByPaymentCode(item.cod_condicao);
              const formattedPrice = formatCurrency({ value: priceCond });

              if (!priceCond && totalQuantity) return '';

              return (
                <S.PaymentCard
                  $isactive={isActive}
                  key={`paymend-card-${ind}`}
                  onClick={() => setSelectedPaymentCode(item.cod_condicao)}
                  data-test={`search-component-payment-button-${item.cod_condicao}`}
                >
                  <S.CheckContent>
                    {isActive ? <S.CheckFill size={24} color={colors.green} /> : <S.CheckBlank size={24} color={colors.grey4} />}
                  </S.CheckContent>

                  <S.Icon size={18} color={isActive ? colors.brand.grafite : colors.gray.gray40} />

                  <S.TitlePaymentForm>
                    {item.desc_condicao}
                    {priceCond > 0 && totalQuantity > 0 && <S.FinalPrice>• {formattedPrice}</S.FinalPrice>}
                  </S.TitlePaymentForm>
                </S.PaymentCard>
              );
            })}
          </S.ListBg>

          <S.BtnContainer onClick={setGlobalPaymentCode} data-test="component-cart-checkout-block-set-payment-confirm">
            <S.ButtonModal>Continuar</S.ButtonModal>
          </S.BtnContainer>
        </S.ModalContent>
      </Modal>
    </S.Container>
  );
};

export default CartCheckoutBlock;
